import * as React from 'react';
import { Input as TextInput } from '../../../components/ui';
import {SortableContainer, SortableElement} from 'react-sortable-hoc';
import {
  WorkoutGroupContainer,
  WorkoutGroupContainerHead,
  WorkoutGroupContainerMinified,
  WorkoutGroupContentEmpty,
  WorkoutToggleContainer,
} from './WorkoutGroups.style';
import {WorkoutComponent, WorkoutEmptyComponent} from './WorkoutComponent';
import {WorkoutGroupMediaComponent} from './WorkoutMediaComponent';
import {
  InputEvent,
  WorkoutGroupProps,
  ExerciseWorkoutSelectProps,
  FileProps,
} from '../../types';

import {WorkoutContainerHead} from './WorkoutComponent.style';
import {
  ButtonDeleteComponent,
  ButtonToggleContentClean,
  ButtonState,
} from '../button';
import {ButtonRowHeaderContainer} from '../button/Button.style';
import {TranslateMediaValueToFileProps} from '../../../utils';

type OnChangeProps = {(event, index?): void};

type DeleteWorkoutGroupProps = {
  (workoutGroup: WorkoutGroupComponentProps, workoutIndex: number): void;
};

/**
 * WorkoutGroupComponent Props
 *
 * Migration Notes:
 * - Previously used WorkoutRoutineContext for updateWorkoutGroup, deleteWorkoutGroup, etc.
 * - Now accepts these as props directly
 * - Requirements: 7.3 - Remove legacy context dependencies
 */
type WorkoutGroupComponentProps = {
  index: number;
  updateWorkoutGroup?: OnChangeProps;
  values?: WorkoutGroupProps;
  workoutGroup: WorkoutGroupProps;
  errors?: any;
  deleteWorkoutGroup?: (workoutGroup: any) => void;
  exerciseListDefault: ExerciseWorkoutSelectProps[];
  setShowDetails: (value: boolean) => void;
  onWorkoutSortEnd?: (params: {
    workoutGroup: any;
    oldIndex: number;
    newIndex: number;
  }) => void;
  updateWorkoutGroupPosition?: (
    direction: string,
    workoutGroup: any,
    currentIndex: number,
  ) => void;
};

/**
 * WorkoutGroupComponent
 * Legacy component for displaying and editing a workout group
 *
 * Note: This component is kept for backward compatibility but is no longer
 * used by the main WorkoutRoutineForm flow, which uses WorkoutGroupField instead.
 */
export const WorkoutGroupComponent = ({
  index,
  workoutGroup,
  errors = {},
  exerciseListDefault,
  setShowDetails,
  updateWorkoutGroup,
  deleteWorkoutGroup,
  onWorkoutSortEnd,
  updateWorkoutGroupPosition,
}: WorkoutGroupComponentProps): JSX.Element => {
  const onChangeInput = (event) => {
    const newWorkoutGroup = {
      ...workoutGroup,
      ...{[event.target.name]: event.target.value},
    };
    if (updateWorkoutGroup) {
      updateWorkoutGroup(newWorkoutGroup);
    }
  };

  const WorkoutItem = ({workout, workoutIndex}) => {
    return (
      <WorkoutComponent
        index={workoutIndex}
        workoutGroupIndex={index}
        workoutGroup={workoutGroup}
        workout={workout}
        errors={(errors?.workoutsAttributes || [])[workoutIndex]}
        exerciseListDefault={exerciseListDefault}
      />
    );
  };

  const RenderWorkoutForms = React.useMemo(
    () =>
      ({workouts}) => {
        return (
          <ul>
            {(workouts || []).map((workout, workoutIndex): JSX.Element => {
              return (
                <li
                  key={`workout-${
                    workout?.id || workout.tempId
                  }-${workoutIndex}-${workoutIndex}`}>
                  <WorkoutItem workout={workout} workoutIndex={workoutIndex} />
                </li>
              );
            })}
          </ul>
        );
      },
    [workoutGroup],
  );

  const [toggleContent, setToggleContent] = React.useState(ButtonState.open);
  const toggleContentWorkoutGroup = (value) => {
    setToggleContent(value);
    setShowDetails(value == ButtonState.open ? true : false);
  };

  const switchToggleContentWorkoutGroup = () => {
    const newState =
      toggleContent === ButtonState.open
        ? ButtonState.closed
        : ButtonState.open;
    setToggleContent(newState);
    setShowDetails(ButtonState.open ? true : false);
  };

  const handleMediaUpdate = (
    mediaType:
      | 'image'
      | 'imageUrl'
      | 'video'
      | 'videoUrl'
      | 'audio'
      | 'audioUrl'
      | 'attachment'
      | 'attachmentUrl'
      | 'cover'
      | 'coverUrl',
    file: FileProps,
  ) => {
    const newWorkoutValue = {...workoutGroup};
    switch (mediaType) {
      case 'coverUrl':
      case 'image':
      case 'imageUrl':
        newWorkoutValue['coverUrl'] = file;
        break;
      case 'videoUrl':
      case 'video':
        newWorkoutValue['videoUrl'] = file;
        break;
      case 'audioUrl':
      case 'audio':
        newWorkoutValue['audioUrl'] = file;
        break;
      case 'attachmentUrl':
      case 'attachment':
        newWorkoutValue['attachmentUrl'] = file;
        break;
    }
    if (updateWorkoutGroup) {
      updateWorkoutGroup(newWorkoutValue);
    }
  };

  const handleMoveUp = () => {
    if (updateWorkoutGroupPosition) {
      updateWorkoutGroupPosition('up', workoutGroup, index);
    }
  };

  const handleMoveDown = () => {
    if (updateWorkoutGroupPosition) {
      updateWorkoutGroupPosition('down', workoutGroup, index);
    }
  };

  const handleDelete = () => {
    if (deleteWorkoutGroup) {
      deleteWorkoutGroup(workoutGroup);
    }
  };

  return (
    <WorkoutGroupContainer>
      <div className={'WorkoutGroupContainer'} style={{flex: 1}}>
        <WorkoutGroupContainerHead>
          <WorkoutToggleContainer>
            <i onClick={handleMoveUp} className="far fa-long-arrow-up" />
            <i onClick={handleMoveDown} className="far fa-long-arrow-down" />
          </WorkoutToggleContainer>
          <TextInput
            s={12}
            id={`workoutGroup-${index}`}
            name={`name`}
            onChange={onChangeInput}
            value={workoutGroup.name}
            placeholder={`Escreva o nome do treino para criar um treino novo para esta rotina`}
            inputClassName={errors?.name ? 'invalid' : ' '}
            error={errors?.name}
            label={null}
          />
          <ButtonRowHeaderContainer>
            <ButtonToggleContentClean
              onPress={toggleContentWorkoutGroup}
              buttonState={toggleContent}
              title="Minimize/Maximize o conteúdo para editar mais facilmente"
            />
            <ButtonDeleteComponent
              onPress={handleDelete}
              name="Treino"
              title="Apagar o treino, suas etapas e exercício"
            />
          </ButtonRowHeaderContainer>
        </WorkoutGroupContainerHead>
        {toggleContent === ButtonState.open && (
          <div className="groupContent">
            <WorkoutGroupMediaComponent
              handleMediaUpdate={handleMediaUpdate}
              image={TranslateMediaValueToFileProps(
                (workoutGroup.coverUrl ||
                  workoutGroup.cover) as unknown as FileProps,
              )}
              video={TranslateMediaValueToFileProps(
                (workoutGroup.videoUrl ||
                  workoutGroup.video) as unknown as FileProps,
                workoutGroup.externalVideoUrl as unknown as any,
              )}
              audio={TranslateMediaValueToFileProps(
                (workoutGroup.audioUrl ||
                  workoutGroup.audio) as unknown as FileProps,
              )}
              attachment={TranslateMediaValueToFileProps(
                (workoutGroup.attachmentUrl ||
                  workoutGroup.attachment) as unknown as FileProps,
              )}
            />
            <div className="row">
              <div className="col s12">
                <div className="flow-text">Blocos do Treino</div>
              </div>
            </div>
            <div className="row">
              <div className="col s12">
                <RenderWorkoutForms
                  workouts={workoutGroup?.workouts?.filter(
                    (workout) => !workout?._destroy,
                  )}
                  onSortEnd={(data) =>
                    onWorkoutSortEnd &&
                    onWorkoutSortEnd({workoutGroup, ...data})
                  }
                  pressDelay={200}
                />
                <WorkoutEmptyComponent
                  workoutGroup={workoutGroup}
                  workoutGroupIndex={index}
                />
              </div>
            </div>
          </div>
        )}
        {toggleContent === ButtonState.closed && (
          <WorkoutGroupContainerMinified
            onClick={switchToggleContentWorkoutGroup}>
            <i className="far fa-ellipsis-h"></i>
          </WorkoutGroupContainerMinified>
        )}
      </div>
    </WorkoutGroupContainer>
  );
};

type WorkoutGroupEmptyComponentProps = {
  createWorkoutGroup?: () => void;
};

/**
 * WorkoutGroupEmptyComponent
 * Legacy component for adding a new workout group
 */
export const WorkoutGroupEmptyComponent = ({
  createWorkoutGroup,
}: WorkoutGroupEmptyComponentProps): JSX.Element => {
  const handleClick = () => {
    if (createWorkoutGroup) {
      createWorkoutGroup();
    }
  };

  return (
    <WorkoutGroupContainer className="clean-top add-new" onClick={handleClick}>
      <WorkoutGroupContentEmpty>Adicionar novo Treino</WorkoutGroupContentEmpty>
    </WorkoutGroupContainer>
  );
};
